import React, { useState } from 'react';
import { Box, Stack, IconButton, Typography, Divider, Grid, Paper, Chip, Rating, Button, Card, FormControl, Select, MenuItem, CircularProgress, TextField } from "@mui/material";
import { ArrowBack, Edit, EmojiEvents, RateReview, ChatBubbleOutline, Person, Badge, History, Info, Close, Save } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";

function ViewAppraisal() {
    const navigate = useNavigate();
    const [isEditing, setIsEditing] = useState(false);

    const [appraisalData, setAppraisalData] = useState({
        status: "In Progress",
        strengths: "Lorem ipsum dolor sit amet, consectetur adipiscing elit...",
        summary: "Lorem ipsum dolor sit amet, consectetur adipiscing elit...",
        comments: "Lorem ipsum dolor sit amet, consectetur adipiscing elit...",
        competencies: [
            { name: "Technical Accuracy", rating: 3 },
            { name: "Deadline Adherence", rating: 4 },
            { name: "Communication", rating: 2 }
        ]
    });

    const totalStars = appraisalData.competencies.reduce((acc, curr) => acc + curr.rating, 0);
    const maxPossibleStars = appraisalData.competencies.length * 5;
    const progressValue = (totalStars / maxPossibleStars) * 100;

    // 3. Helper to get color based on percentage thresholds
    const getProgressColor = (value) => {
        if (value <= 25) return '#ef4444'; // Red
        if (value <= 50) return '#f59e0b'; // Orange
        if (value <= 75) return '#3b82f6'; // Blue
        return '#22c55e'; // Green
    };
    const metaItems = [
        { label: "Appraisal ID", val: "APP0012", icon: <History sx={{ fontSize: 18 }} />, isEditable: false },
        { label: "Employee Name", val: "Bharath M", icon: <Person sx={{ fontSize: 18 }} />, isEditable: false },
        { label: "Employee ID", val: "EMP0012", icon: <Badge sx={{ fontSize: 18 }} />, isEditable: false },
        { label: "Created By", val: "Jay", icon: <Edit sx={{ fontSize: 18 }} />, isEditable: false },
        { label: "Created At", val: "12 March 2026", icon: <History sx={{ fontSize: 18 }} />, isEditable: false },
        {
            label: "Status",
            val: "In Progress",
            name: "status",
            icon: <Info sx={{ fontSize: 18 }} />,
            isChip: true,
            isEditable: true,
            options: ["In Progress", "Completed", "On Hold"],
            colors: { bg: '#fff7ed', text: '#c2410c' } // Orange-ish for In Progress
        },
    ];

    const handleFieldChange = (field, value) => {
        setAppraisalData(prev => ({ ...prev, [field]: value }));
    };

    const handleRatingChange = (index, newValue) => {
        const updatedCompetencies = [...appraisalData.competencies];
        updatedCompetencies[index].rating = newValue;
        setAppraisalData(prev => ({ ...prev, competencies: updatedCompetencies }));
    };

    return (
        <Box sx={{ p: 1 }}>
            <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ pb: 1, borderBottom: '1px solid #c3c3c3' }}>
                <Stack direction="row" alignItems="center">
                    <IconButton onClick={() => navigate('/manager/performance-management', { state: { activeTab: 1 } })} sx={{ color: '#64748b' }}>
                        <ArrowBack sx={{ fontSize: 20 }} />
                    </IconButton>
                    <Divider orientation="vertical" flexItem sx={{ mx: 2, height: 24, my: 'auto' }} />
                    <Box>
                        <Typography variant="h6" fontWeight="700" color="#1e293b">Appraisal Details</Typography>
                        <Typography variant="caption" color="text.secondary">
                            View performance breakdown for <b>Q1 2026</b>
                        </Typography>
                    </Box>
                </Stack>
                <Stack direction="row" spacing={1}>
                    {!isEditing ? (
                        <Button variant="outlined" startIcon={<Edit />} onClick={() => setIsEditing(true)}>Edit</Button>
                    ) : (
                        <>
                            <Button variant="text" color="inherit" startIcon={<Close />} onClick={() => setIsEditing(false)}>Cancel</Button>
                            <Button variant="contained" startIcon={<Save />} onClick={() => setIsEditing(false)}>Save Changes</Button>
                        </>
                    )}
                </Stack>
            </Stack>

            <Grid container spacing={3} mt={2}>
                <Grid size={{ xs: 12, md: 8.5 }}>
                    <Stack spacing={4}>
                        <Grid container spacing={2}>
                            {metaItems.map((item, idx) => (
                                <Grid size={{ xs: 12, sm: 6 }} key={idx}>
                                    <Stack direction="row" spacing={2} alignItems="center">
                                        <Box sx={{ color: '#94a3b8', display: 'flex' }}>{item.icon}</Box>
                                        <Box sx={{ minWidth: 120 }}>
                                            <Typography variant="caption" display="block" sx={{ color: '#64748b', fontWeight: 500, textTransform: 'uppercase' }}>{item.label}</Typography>
                                            {isEditing && item.isEditable ? (
                                                <FormControl size="small" fullWidth sx={{ mt: 0.5 }}>
                                                    <Select
                                                        value={item.val}
                                                        onChange={(e) => handleFieldChange(item.name, e.target.value)}
                                                        sx={{ height: 32, fontSize: '0.875rem' }}
                                                    >
                                                        {item.options.map(o => <MenuItem key={o} value={o}>{o}</MenuItem>)}
                                                    </Select>
                                                </FormControl>
                                            ) : item.isChip ? (
                                                <Chip label={item.val} size="small" sx={{ bgcolor: item.val === 'Completed' ? '#f0fdf4' : '#fff7ed', color: item.val === 'Completed' ? '#15803d' : '#c2410c', fontWeight: 700, mt: 0.5 }} />
                                            ) : (
                                                <Typography variant="body2" sx={{ fontWeight: 600, color: '#1e293b' }}>{item.val}</Typography>
                                            )}
                                        </Box>
                                    </Stack>
                                </Grid>
                            ))}
                        </Grid>

                        {/* Cards Section */}
                        <Stack spacing={2}>
                            {[
                                { title: "Key Strengths & Expectations", icon: <EmojiEvents />, field: "strengths", color: "#0369a1", bg: "#f0f9ff" },
                                { title: "Summary", icon: <RateReview />, field: "summary", color: "#0369a1", bg: "#f0f9ff" },
                                { title: "Comments", icon: <ChatBubbleOutline />, field: "comments", color: "#0369a1", bg: "#f0f9ff" }
                            ].map((section) => (
                                <Card key={section.field} variant="outlined" sx={{ borderRadius: 2 }}>
                                    <Box sx={{ p: 1.5, bgcolor: section.bg, display: 'flex', gap: 1 }}>
                                        {React.cloneElement(section.icon, { sx: { color: section.color } })}
                                        <Typography variant="subtitle2" sx={{ fontWeight: 700, color: section.color }}>{section.title}</Typography>
                                    </Box>
                                    <Box sx={{ p: 2 }}>
                                        {isEditing ? (
                                            <TextField
                                                fullWidth
                                                multiline
                                                rows={3}
                                                variant="outlined"
                                                value={appraisalData[section.field]}
                                                onChange={(e) => handleFieldChange(section.field, e.target.value)}
                                            />
                                        ) : (
                                            <Typography variant="body2">{appraisalData[section.field]}</Typography>
                                        )}
                                    </Box>
                                </Card>
                            ))}
                        </Stack>
                    </Stack>
                </Grid>

                {/* Right Sidebar - Competencies */}
                <Grid size={{ xs: 12, md: 3.5 }}>
                    <Paper variant="outlined" sx={{ p: 2.5, borderRadius: 2, bgcolor: '#fcfcfc' }}>
                        <Typography variant="subtitle1" fontWeight="700" mb={2} textAlign="left">
                            Performance Score
                        </Typography>

                        <Box sx={{ display: 'flex', justifyContent: 'center', width: '100%', mb: 3 }}>
                            <Box sx={{ position: 'relative', display: 'inline-flex', width: 100, height: 100 }}>
                                <CircularProgress variant="determinate" value={100} size={100} thickness={5} sx={{ color: '#e2e8f0' }} />
                                <CircularProgress
                                    variant="determinate"
                                    value={progressValue}
                                    size={100}
                                    thickness={5}
                                    sx={{
                                        color: getProgressColor(progressValue),
                                        position: 'absolute',
                                        left: 0,
                                        [`& .MuiCircularProgress-circle`]: { strokeLinecap: 'round' }
                                    }}
                                />
                                <Box sx={{ top: 0, left: 0, bottom: 0, right: 0, position: 'absolute', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                                    <Typography variant="h6" fontWeight="700">{Math.round(progressValue)}%</Typography>
                                </Box>
                            </Box>
                        </Box>
                        <Typography variant="subtitle2" fontWeight="700" mb={2}>Core Competencies</Typography>
                        <Stack spacing={2.5}>
                            {appraisalData.competencies.map((skill, index) => (
                                <Box key={skill.name}>
                                    <Typography variant="caption" sx={{ color: '#64748b', fontWeight: 600 }}>{skill.name}</Typography>
                                    <Rating
                                        value={skill.rating}
                                        readOnly={!isEditing}
                                        size="small"
                                        sx={{ display: 'flex', mt: 0.5 }}
                                        onChange={(event, newValue) => handleRatingChange(index, newValue)}
                                    />
                                </Box>
                            ))}
                        </Stack>
                    </Paper>
                </Grid>
            </Grid>
        </Box>
    );
}

export default ViewAppraisal;